import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveAreaBump } from '@nivo/bump';
import { ResponsiveTreeMap } from '@nivo/treemap';
import { ResponsiveFunnel } from '@nivo/funnel';

// Nivo data types
interface NivoLineSeries {
  id: string;
  data: Array<{ x: string | number; y: number }>;
}

interface NivoBarData {
  [key: string]: string | number;
}

interface NivoAreaBumpSeries {
  id: string;
  data: Array<{ x: string; y: number }>;
  [key: string]: unknown;
}

interface NivoTreeMapNode {
  name: string;
  value?: number;
  children?: NivoTreeMapNode[];
  [key: string]: unknown;
}

interface NivoFunnelData {
  id: string;
  value: number;
  label?: string;
}

interface BaseChartConfig {
  height: number;
  width?: number;
  title?: string;
  margin?: { top: number; right: number; bottom: number; left: number };
  axisLeftLabel?: string;
}

interface LineChartConfig extends BaseChartConfig {
  type: 'line';
  data: NivoLineSeries[];
  xScale?: { type: 'point' | 'linear' | 'time' | 'log' };
  yScale?: { type: 'linear' | 'log'; min: 'auto' | number; max: 'auto' | number };
  curve?: 'linear' | 'monotoneX' | 'monotoneY' | 'cardinal' | 'natural';
  colors?: string[];
  enableGridX?: boolean;
  enableGridY?: boolean;
  lineWidth?: number;
  pointSize?: number;
  useMesh?: boolean;
  enableDualYAxis?: boolean;
}

interface BarChartConfig extends BaseChartConfig {
  type: 'bar';
  data: NivoBarData[];
  keys: string[];
  indexBy: string;
  colors?: string[];
  layout?: 'vertical' | 'horizontal';
  enableGridX?: boolean;
  enableGridY?: boolean;
  enableLabel?: boolean;
  groupMode?: 'grouped' | 'stacked';
}

interface AreaBumpChartConfig extends BaseChartConfig {
  type: 'areabump';
  data: NivoAreaBumpSeries[];
  colors?: string[];
  enableGridX?: boolean;
  enableGridY?: boolean;
  interpolation?: 'smooth' | 'linear';
  fillOpacity?: number;
}

interface TreeMapChartConfig extends BaseChartConfig {
  type: 'treemap';
  data: NivoTreeMapNode;
  colors?: string[];
  tile?: 'binary' | 'dice' | 'slice' | 'sliceDice' | 'squarify' | 'resquarify';
  leavesOnly?: boolean;
  innerPadding?: number;
  outerPadding?: number;
  enableLabel?: boolean;
  labelSkipSize?: number;
}

interface FunnelChartConfig extends BaseChartConfig {
  type: 'funnel';
  data: NivoFunnelData[];
  colors?: string[];
  enableLabel?: boolean;
  labelColor?: string;
  spacing?: number;
  shapeBlending?: number;
  direction?: 'horizontal' | 'vertical';
}

type GraphConfig = LineChartConfig | BarChartConfig | AreaBumpChartConfig | TreeMapChartConfig | FunnelChartConfig;

interface GraphProps {
  config: GraphConfig;
  className?: string;
}

const defaultMargin = { top: 50, right: 140, bottom: 80, left: 90 };

const chartTheme = {
  text: { fontFamily: 'Montserrat, sans-serif', fontWeight: 400 as const },
  axis: { ticks: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 11 } }, legend: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 12 } } },
  legends: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 10, dominantBaseline: 'middle' as const, dy: 4 } },
  labels: { text: { fontFamily: 'Montserrat, sans-serif', fontSize: 11 } },
};

export const Graph: React.FC<GraphProps> = ({ config, className = '' }) => {
  if (!config || !config.data || config.data.length === 0) {
    return (
      <div className={`w-full h-64 flex items-center justify-center text-gray-500 ${className}`}>
        No data available for chart
      </div>
    );
  }

  // Handle "no data" charts with custom message
  const isNoDataChart = 'no_data_message' in config;
  if (isNoDataChart) {
    return (
      <div className={`w-full relative z-10 ${className}`}>
        {config.title && (
          <h3 className="text-lg font-semibold mb-4 text-center text-gray-900">
            {config.title}
          </h3>
        )}
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div
            className="flex flex-col items-center justify-center text-gray-500 bg-gray-50 rounded border-2 border-dashed border-gray-300"
            style={{ height: config.height }}
          >
            <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-sm font-medium text-gray-700 mb-1">No Chart Data</p>
            <p className="text-xs text-gray-500 text-center max-w-sm">
              {config.no_data_message as React.ReactNode || 'No data available for this chart'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const margin = config.margin || defaultMargin;

  const renderChart = () => {
    if (config.type === 'line') {
      const lineConfig = config as LineChartConfig;
      return (
        <ResponsiveLine
          data={lineConfig.data}
          theme={chartTheme}
          margin={{ ...margin, right: 20, bottom: 60 }}
          xScale={lineConfig.xScale || { type: 'point' }}
          yScale={lineConfig.yScale || { type: 'linear', min: 'auto', max: 'auto' }}
          curve={lineConfig.curve || 'monotoneX'}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: lineConfig.axisLeftLabel || undefined,
            legendOffset: -50,
            legendPosition: 'middle'
          }}
          colors={lineConfig.colors || ['#290BD9', '#D1D5DB', '#059669']}
          lineWidth={lineConfig.lineWidth || 2}
          pointSize={lineConfig.pointSize || 5}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh={lineConfig.useMesh !== false}
          enableGridX={lineConfig.enableGridX !== false}
          enableGridY={lineConfig.enableGridY !== false}
          legends={[
            {
              anchor: 'bottom',
              direction: 'row',
              justify: false,
              translateX: 0,
              translateY: 50,
              itemsSpacing: 20,
              itemDirection: 'left-to-right',
              itemWidth: 120,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 10,
              symbolSpacing: 6,
              symbolShape: 'circle',
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          tooltip={({ point }) => (
            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
              <div className="font-medium text-gray-900">{point.seriesId}</div>
              <div className="text-sm text-gray-700">
                {point.data.xFormatted}: {point.data.yFormatted}
              </div>
            </div>
          )}
        />
      );
    }

    if (config.type === 'bar') {
      const barConfig = config as BarChartConfig;

      // Custom label layer: positions labels above bars when they're too short
      const SmartLabelLayer = (props: { bars: readonly { x: number; y: number; width: number; height: number; data: { value: number | null }; color: string }[] }) => (
        <g>
          {props.bars.map((bar, i) => {
            const isSmall = bar.height < 28;
            return (
              <text
                key={i}
                x={bar.x + bar.width / 2}
                y={isSmall ? bar.y - 6 : bar.y + bar.height / 2}
                textAnchor="middle"
                dominantBaseline={isSmall ? 'auto' : 'central'}
                fontFamily="Montserrat, sans-serif"
                fontSize={11}
                fontWeight={600}
                fill={isSmall ? '#374151' : '#ffffff'}
              >
                {bar.data.value}
              </text>
            );
          })}
        </g>
      );

      return (
        <ResponsiveBar
          data={barConfig.data}
          keys={barConfig.keys}
          indexBy={barConfig.indexBy}
          theme={chartTheme}
          margin={{ ...margin, right: 20, bottom: 60 }}
          padding={0.3}
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          groupMode={barConfig.groupMode || 'stacked'}
          colors={barConfig.colors || ['#290BD9', '#D1D5DB', '#059669']}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={barConfig.layout === 'horizontal' ? null : {
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: barConfig.axisLeftLabel || undefined,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={barConfig.enableGridX !== false}
          enableGridY={barConfig.enableGridY !== false}
          enableLabel={false}
          layers={['grid', 'axes', 'bars', SmartLabelLayer, 'markers', 'legends']}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom',
              direction: 'row',
              justify: false,
              translateX: 0,
              translateY: 50,
              itemsSpacing: 20,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: 10,
              symbolSpacing: 6,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1
                  }
                }
              ]
            }
          ]}
          tooltip={({ value, color, indexValue, data }) => (
            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg" style={{ maxWidth: '2000px' }}>
              <div className="font-medium text-gray-900" style={{ whiteSpace: 'nowrap' }}>
                {data.fullName || indexValue}
              </div>
              <div className="flex items-center gap-2 text-sm mt-1">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: color }}
                />
                <span>CTR: {typeof value === 'number' ? value.toFixed(2) : value}%</span>
              </div>
              {data.clicks && data.impressions && (
                <div className="text-xs text-gray-700 mt-1">
                  {data.clicks.toLocaleString()} clicks / {data.impressions.toLocaleString()} impressions
                </div>
              )}
            </div>
          )}
        />
      );
    }

    if (config.type === 'areabump') {
      const areaBumpConfig = config as AreaBumpChartConfig;
      return (
        <ResponsiveAreaBump
          data={areaBumpConfig.data}
          theme={chartTheme}
          margin={margin}
          spacing={8}
          colors={areaBumpConfig.colors || ['#8884d8', '#82ca9d', '#ff7300']}
          blendMode="multiply"
          fillOpacity={areaBumpConfig.fillOpacity || 0.6}
          activeFillOpacity={0.8}
          borderWidth={0}
          activeBorderWidth={3}
          borderColor={{ theme: 'background' }}
          borderOpacity={0.4}
          startLabel={false}
          endLabel={false}
          axisTop={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
          }}
          tooltip={({ serie }) => (
            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{serie.id}</span>
              </div>
            </div>
          )}
        />
      );
    }

    if (config.type === 'treemap') {
      const treeMapConfig = config as TreeMapChartConfig;
      return (
        <ResponsiveTreeMap
          data={treeMapConfig.data}
          theme={chartTheme}
          margin={margin}
          identity="name"
          value="value"
          tile={treeMapConfig.tile && treeMapConfig.tile !== 'resquarify' ? treeMapConfig.tile : 'squarify'}
          leavesOnly={treeMapConfig.leavesOnly !== false}
          innerPadding={treeMapConfig.innerPadding || 3}
          outerPadding={treeMapConfig.outerPadding || 3}
          colors={treeMapConfig.colors || { scheme: 'nivo' }}
          borderWidth={2}
          borderColor={{ from: 'color', modifiers: [['darker', 0.1]] }}
          enableLabel={treeMapConfig.enableLabel !== false}
          label={(node) => `${node.value?.toLocaleString() || 0}`}
          labelSkipSize={0}
          labelTextColor={{ from: 'color', modifiers: [['darker', 1.2]] }}
          orientLabel={false}
          animate={false}
          motionConfig="gentle"
          tooltip={({ node }) => (
            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
              <div className="font-medium text-gray-900">{node.data.name as React.ReactNode}</div>
              <div className="text-sm text-gray-700">
                <div>Conversions: {node.value?.toLocaleString() || 0}</div>
                {node.data.cost !== undefined && node.data.cost !== null && typeof node.data.cost === 'number' && node.data.cost > 0 && (
                  <div>Cost: ${node.data.cost.toFixed(2)}</div>
                )}
                {node.data.ctr !== undefined && (
                  <div>CTR: {node.data.ctr as React.ReactNode}%</div>
                )}
                {node.data.cvr !== undefined && (
                  <div>CVR: {node.data.cvr as React.ReactNode}%</div>
                )}
              </div>
            </div>
          )}
        />
      );
    }

    if (config.type === 'funnel') {
      const funnelConfig = config as FunnelChartConfig;
      return (
        <ResponsiveFunnel
          // @nivo/funnel 0.99's FunnelDatum requires a string index signature;
          // NivoFunnelData is structurally compatible (id/value/label only).
          data={funnelConfig.data as unknown as React.ComponentProps<typeof ResponsiveFunnel>['data']}
          theme={chartTheme}
          margin={margin}
          direction={funnelConfig.direction || 'horizontal'}
          spacing={funnelConfig.spacing || 0}
          shapeBlending={funnelConfig.shapeBlending || 0.66}
          colors={funnelConfig.colors || ['#ef4444', '#f97316', '#eab308']}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
          enableLabel={funnelConfig.enableLabel !== false}
          labelColor={funnelConfig.labelColor || '#ffffff'}
          beforeSeparatorLength={40}
          beforeSeparatorOffset={20}
          afterSeparatorLength={40}
          afterSeparatorOffset={20}
          currentPartSizeExtension={10}
          currentBorderWidth={40}
          motionConfig="gentle"
          tooltip={({ part }) => (
            <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
              <div className="font-medium text-gray-900">{part.data.label || part.data.id}</div>
              <div className="text-sm text-gray-700">
                Value: {part.formattedValue}
              </div>
            </div>
          )}
        />
      );
    }

    return <div className="text-red-500">Unsupported chart type</div>;
  };

  return (
    <div className={`w-full relative z-10 ${className}`}>
      {config.title && (
        <h3 className="text-lg font-semibold mb-4 text-center text-gray-900">
          {config.title}
        </h3>
      )}
      <div className="bg-white p-4 rounded-lg border border-gray-200 overflow-x-auto">
        <div style={{ height: config.height, width: config.width || '100%', minWidth: config.width || undefined }}>
          {renderChart()}
        </div>
      </div>
    </div>
  );
};